/**
 * 시각 표기 (19회차, 후보 #11·C-8).
 *
 * 16회차까지 표기가 세 가지 섞여 있었다.
 *   `toLocaleString('ko-KR')` 일곱 곳 → "2026. 9. 7. 오후 3:31:12"
 *   `toLocaleTimeString('ko-KR')` 두 곳 → "오후 3:31:12"
 *   캔버스 눈금 자체 구현 → "31:12"
 * 오전·오후가 붙은 12시간제는 표에서 자리를 많이 먹고 정렬해 읽기 어렵다.
 * 관제 화면은 **24시간제 고정 폭**이 맞다.
 *
 * 상대 표기("3분 전")는 어디에도 없었다. 목록에서 절대 시각을 보고 지금과 얼마나 떨어졌는지
 * 머리로 빼는 일을 없앤다. 절대 시각은 툴팁으로 남긴다.
 *
 * 전부 순수 함수다. `now`를 받도록 해 두어 시험할 수 있다.
 */

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

/** `HH:mm:ss`. 오늘 안에서 흐르는 값(실시간·DB 세션)에 쓴다 */
export function formatClock(at: number): string {
  const date = new Date(at);
  return `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

/** `M/D HH:mm:ss`. 며칠에 걸칠 수 있는 목록(검색 결과·이력)에 쓴다 */
export function formatStamp(at: number): string {
  const date = new Date(at);
  return `${date.getMonth() + 1}/${date.getDate()} ${formatClock(at)}`;
}

/** `YYYY-MM-DD HH:mm:ss`. 툴팁처럼 한 건만 정확히 보일 때 */
export function formatFull(at: number): string {
  const date = new Date(at);
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${formatClock(at)}`;
}

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * "3분 전". 30초 안쪽은 "방금", 하루가 넘으면 날짜를 그대로 보인다.
 * 미래 시각(시계 어긋남)은 "곧"으로 접는다. "-3분 전"이 뜨면 사람이 오해한다.
 */
export function formatRelative(at: number, now: number = Date.now()): string {
  const diff = now - at;
  if (diff < -30_000) return '곧';
  if (diff < 30_000) return '방금';
  if (diff < HOUR) return `${Math.round(diff / MINUTE)}분 전`;
  if (diff < DAY) return `${Math.floor(diff / HOUR)}시간 전`;
  if (diff < 7 * DAY) return `${Math.floor(diff / DAY)}일 전`;
  return formatStamp(at);
}

/** 기간. 1초 미만은 ms, 1분을 넘으면 분·초 */
export function formatDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return '-';
  if (ms < 1_000) return `${Math.round(ms)}ms`;
  if (ms < 60_000) return `${(ms / 1_000).toFixed(1)}초`;
  const minutes = Math.floor(ms / 60_000);
  const seconds = Math.round((ms % 60_000) / 1_000);
  return `${minutes}분 ${seconds}초`;
}

/** 지속 시간을 사람 말로. 초 단위 아래는 버린다(알림이 몇 초 단위로 흐른다) */
export function formatSpan(ms: number): string {
  const seconds = Math.max(0, Math.round(ms / 1_000));
  if (seconds < 60) return `${seconds}초`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    const rest = seconds % 60;
    return rest === 0 ? `${minutes}분` : `${minutes}분 ${rest}초`;
  }
  const hours = Math.floor(minutes / 60);
  const restMinutes = minutes % 60;
  return restMinutes === 0 ? `${hours}시간` : `${hours}시간 ${restMinutes}분`;
}

/** 숫자. 세 자리마다 쉼표 */
export function formatCount(value: number): string {
  return value.toLocaleString('ko-KR');
}

/**
 * 비율(0~1)을 백분율로. 0은 "0%", 아주 작은 값은 "<0.1%"다 (34회차에 한자리로 모았다).
 * 0.0%로 적으면 오류가 없는 것과 아주 적은 것이 같아 보인다
 */
export function formatPercent(value: number): string {
  if (value > 0 && value < 0.001) return '<0.1%';
  return `${(value * 100).toFixed(value === 0 ? 0 : 1)}%`;
}

const UNITS = ['B', 'KB', 'MB', 'GB'];

export function formatBytes(bytes: number): string {
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < UNITS.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(unit === 0 ? 0 : 1)}${UNITS[unit]}`;
}

/** 소수 한 자리 ms. 게이지 숫자가 자리를 옮겨 다니지 않게 한다 */
export function formatMs(ms: number): string {
  return `${ms.toFixed(1)}ms`;
}

/**
 * epoch ms → `datetime-local` 값. 브라우저 로컬 시각 기준.
 * 검색 폼(7회차)에 있던 것을 여기로 옮겨 기간 입력이 있는 화면 전부가 같은 함수를 쓴다.
 *
 * **초까지 적는다** (23-A회차 리뷰 6). 분까지만 적으면 추이 구간이나 앞뒤 요청 링크가 준
 * 초 단위 기간이 폼을 거치며 뭉개진다(10:00:45 → 10:00:00). 입력칸은 `step="1"`이라
 * 브라우저도 초를 보여 준다. ms는 여전히 버려지므로, 링크로 들어온 **첫 조회**는
 * 폼이 아니라 주소의 epoch를 그대로 쓴다(`SearchPanel`).
 */
export function toLocalInput(at: number): string {
  const date = new Date(at);
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
  );
}

/** `datetime-local` 값 → epoch ms. 비었거나 모양이 아니면 undefined */
export function fromLocalInput(value: string): number | undefined {
  if (value === '') return undefined;
  const at = new Date(value).getTime();
  return Number.isFinite(at) ? at : undefined;
}
